import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { FeedbackServiceFactory } from '../../api/FeedbackService';
import {
  FeedbackRating,
  FeedbackRatingSystem,
  FeedbackSource,
} from '../feedback-service/FeedbackTypes';
import { useCurrentUser } from '../../contexts/UserContext';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';
import { log } from '../../loggers/Logger';
import { useLoadedContentId } from '../v2-hooks/useLoadedContentId';

/** Read-only presentation state for the like/dislike feedback control. */
export interface ContentFeedbackViewModel {
  /** Whether the feedback control should be shown (publication-mode documents only). */
  readonly isVisible: boolean;
  /** True while the initial counts/user-vote are loading. */
  readonly isLoading: boolean;
  /** Current like count. */
  readonly likeCount: number;
  /** Current dislike count. */
  readonly dislikeCount: number;
  /** The user's current rating, or `undefined` when they have not rated. */
  readonly userRating: FeedbackRating | undefined;
  /** Whether a like/dislike submission is in flight. */
  readonly isVoting: boolean;
  /** Cast (or retract, when re-clicking the same rating) a like or dislike. */
  readonly vote: (rating: FeedbackRating) => void;
  /** Whether the post-vote "tell us more" prompt is shown; only after casting (not retracting) a rating. */
  readonly isFeedbackPromptVisible: boolean;
  /** The rating the prompt is asking about; `undefined` while the prompt is hidden. */
  readonly promptRating: FeedbackRating | undefined;
  /** Dismiss the post-vote feedback prompt. */
  readonly dismissFeedbackPrompt: () => void;
  /** Navigate to the written-feedback form for the just-cast rating, then dismiss the prompt. */
  readonly submitWrittenFeedback: () => void;
}

function countFor(
  counts: readonly { readonly value: string; readonly count: number }[] | undefined,
  rating: FeedbackRating,
): number {
  return counts?.find((c) => c.value === rating)?.count ?? 0;
}

function isFeedbackRating(value: string): value is FeedbackRating {
  return Object.values(FeedbackRating).some((rating) => rating === value);
}

/** Narrows the service's `string` rating to a known {@link FeedbackRating}; `undefined` when unset or unrecognized. */
function toFeedbackRating(rating: string | undefined): FeedbackRating | undefined {
  return rating !== undefined && isFeedbackRating(rating) ? rating : undefined;
}

/**
 * Drives the like/dislike control on the read-only view page. Loads the
 * aggregate counts and the current user's vote, and toggles the user's vote
 * on click. Visible only when the document is loaded in publication mode.
 */
export function useContentFeedbackViewModel(): ContentFeedbackViewModel {
  const loadedDocument = useLoadedDocument();
  const { userId } = useCurrentUser();
  const history = useHistory();
  const feedbackClient = useMemo(() => FeedbackServiceFactory.create(), []);
  const contentId = useLoadedContentId();

  const isVisible = loadedDocument?.kind === 'publication';

  const [isLoading, setIsLoading] = useState(true);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [userRating, setUserRating] = useState<FeedbackRating | undefined>(undefined);
  const [isVoting, setIsVoting] = useState(false);
  const [promptRating, setPromptRating] = useState<FeedbackRating | undefined>(undefined);

  // When the control is visible, load the aggregate counts and the user's vote.
  useEffect(() => {
    if (!isVisible || !contentId) {
      setIsLoading(false);
      return;
    }
    let isMounted = true;
    setIsLoading(true);
    void (async () => {
      try {
        const [stats, userItem] = await Promise.all([
          feedbackClient.listFeedback(
            [contentId],
            FeedbackRatingSystem.LikeDislike,
            FeedbackSource.Guidance,
          ),
          feedbackClient.getFeedbackByUser(contentId, userId, FeedbackSource.Guidance),
        ]);
        if (!isMounted) return;
        const counts = stats[0]?.counts;
        setLikeCount(countFor(counts, FeedbackRating.Like));
        setDislikeCount(countFor(counts, FeedbackRating.Dislike));
        setUserRating(toFeedbackRating(userItem?.Rating));
      } catch (err) {
        log.warn({ error: err, contentId }, 'Failed to load content feedback');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [isVisible, contentId, userId, feedbackClient]);

  const vote = useCallback(
    async (rating: FeedbackRating) => {
      if (!contentId) return;
      setIsVoting(true);
      try {
        // Re-clicking the active rating retracts it; otherwise switch/cast.
        if (userRating === rating) {
          await feedbackClient.deleteFeedback(
            contentId,
            FeedbackRatingSystem.LikeDislike,
            FeedbackSource.Guidance,
          );
        } else {
          if (userRating !== undefined) {
            await feedbackClient.deleteFeedback(
              contentId,
              FeedbackRatingSystem.LikeDislike,
              FeedbackSource.Guidance,
            );
          }
          await feedbackClient.submitFeedback(
            contentId,
            userId,
            rating,
            FeedbackRatingSystem.LikeDislike,
            '',
            FeedbackSource.Guidance,
          );
        }

        // Re-read the authoritative counts + user vote after the change.
        const [stats, userItem] = await Promise.all([
          feedbackClient.listFeedback(
            [contentId],
            FeedbackRatingSystem.LikeDislike,
            FeedbackSource.Guidance,
          ),
          feedbackClient.getFeedbackByUser(contentId, userId, FeedbackSource.Guidance),
        ]);
        const counts = stats[0]?.counts;
        setLikeCount(countFor(counts, FeedbackRating.Like));
        setDislikeCount(countFor(counts, FeedbackRating.Dislike));
        const newRating = toFeedbackRating(userItem?.Rating);
        setUserRating(newRating);

        // After landing on a rating (cast or switch, not a retraction), invite written feedback.
        if (newRating !== undefined) setPromptRating(newRating);
      } catch (err) {
        log.error({ err, contentId }, 'Failed to submit content feedback');
      } finally {
        setIsVoting(false);
      }
    },
    [contentId, userId, userRating, feedbackClient],
  );

  const dismissFeedbackPrompt = useCallback(() => setPromptRating(undefined), []);

  const submitWrittenFeedback = useCallback(() => {
    if (!contentId || promptRating === undefined) return;
    history.push(`/publication-feedback/${contentId}/${promptRating}`);
    setPromptRating(undefined);
  }, [contentId, promptRating, history]);

  return {
    isVisible,
    isLoading,
    likeCount,
    dislikeCount,
    userRating,
    isVoting,
    vote,
    isFeedbackPromptVisible: promptRating !== undefined,
    promptRating,
    dismissFeedbackPrompt,
    submitWrittenFeedback,
  };
}
